import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ManifestListApiAdapter } from '../adapters';
import { ManifestFilters, Manifest } from '../models';

export const useManifests = (filters: ManifestFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ManifestListApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['manifests', filters],
    queryFn: ({ pageParam = 1 }) =>
      apiAdapter.getManifests({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage < lastPage.meta.lastPage) {
        return lastPage.meta.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const manifests: Manifest[] =
    data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages?.[0]?.meta?.total ?? 0;

  return {
    manifests,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total,
  };
};
